import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { fetchUserProfile } from "./api";
import "./UserProfile.css";
import usePageTitle from "../../usePageTitle";
import api from "../../../Services/api";

const UserProfile = () => {
  usePageTitle("User Profile | SkeyeForm™");
  const [userProfile, setUserProfile] = useState({
    username: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // eslint-disable-next-line
  const [passwordError, setPasswordError] = useState("");
  // eslint-disable-next-line
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const toast = useRef(null);

  useEffect(() => {
    loadUserProfile();
    // eslint-disable-next-line
  }, []);

  const loadUserProfile = async () => {
    try {
      const data = await fetchUserProfile();
      setUserProfile({
        username: data.user.username,
        email: data.user.email,
      });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load user profile",
        life: 3000,
      });
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All password fields are required");
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "All password fields are required",
          life: 3000,
        });
      }
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match");
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "New passwords don't match",
          life: 3000,
        });
      }
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Password must be at least 8 characters long",
          life: 3000,
        });
      }
      return;
    }

    try {
      setLoading(true);
      const passwordUpdateRequest = {
        currentPassword,
        newPassword,
      };

      // Use the api instance to send a PUT request
      const response = await api.put(
        "/auth/update-password",
        passwordUpdateRequest
      );
      if (response.data.statusCode === 200) {
        setPasswordSuccess(response.data.message);
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response.data.message || "Password updated successfully",
            life: 3000,
          });
        }

        // Clear password fields
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update password";
      setPasswordError(errorMessage);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <Toast ref={toast} />
      <div className="page-header">
        <h1>User Profile</h1>
        <div className="title-bar"></div>
      </div>

      <div className="profile-card">
        <form onSubmit={handlePasswordUpdate} className="profile-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <InputText
              id="username"
              name="username"
              value={userProfile.username}
              disabled
              className="w-full"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              name="email"
              value={userProfile.email}
              disabled
              className="w-full"
            />
          </div>

          <div className="form-group p-fluid">
            <label htmlFor="currentPassword">Current Password</label>
            <Password
              id="currentPassword"
              name="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              toggleMask
              feedback={false}
              className="w-full"
            />
          </div>

          <div className="form-group p-fluid">
            <label htmlFor="newPassword">New Password</label>
            <Password
              id="newPassword"
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              toggleMask
              feedback={true}
              className="w-full"
            />
          </div>

          <div className="form-group p-fluid">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <Password
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              toggleMask
              feedback={false}
              className="w-full"
            />
          </div>
          <div className="form-actions">
            <Button
              type="submit"
              label="Update Password"
              icon="pi pi-save"
              loading={loading}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              className="save-button"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
